import { useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuthStore } from "../../state/authStore";
import { ApiError, auth } from "../../lib/api";
import { AuthLayout, Field, FormError, PrimaryButton } from "./AuthLayout";
import { PasswordInput } from "./PasswordInput";

function mapError(err: unknown): string {
  if (err instanceof ApiError) {
    switch (err.message) {
      case "token_invalid":
        return "Reset-Link ungültig oder abgelaufen. Bitte neu anfordern.";
      case "password_too_short":
        return "Passwort zu kurz — mindestens 10 Zeichen.";
      case "password_too_common":
        return "Passwort zu häufig benutzt — bitte etwas Eigenes wählen.";
      default:
        if (err.kind === "network") return "Keine Verbindung. Versuch's nochmal.";
        return err.message;
    }
  }
  return err instanceof Error ? err.message : String(err);
}

export function ResetPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const token = params.get("token") ?? "";

  const [password, setPassword] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!token) {
    return (
      <AuthLayout title="Neues Passwort" subtitle="Reset-Link unvollständig.">
        <p className="text-sm text-fg-muted">
          Der Link ist unvollständig — es fehlt der <code className="font-mono">token</code>-Parameter.
          Bitte den Link aus der E-Mail komplett kopieren.
        </p>
        <Link
          to="/forgot"
          className="block text-center min-h-[44px] py-2 rounded-lg text-accent hover:underline"
        >
          Neuen Link anfordern
        </Link>
      </AuthLayout>
    );
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < 10) {
      setError("Passwort muss mindestens 10 Zeichen lang sein.");
      return;
    }
    setPending(true);
    try {
      const { user } = await auth.reset(token, password);
      // Server set a session cookie; sync local auth store and route home.
      useAuthStore.setState({ status: "authed", user, errorMessage: null });
      navigate("/", { replace: true });
    } catch (err) {
      setError(mapError(err));
    } finally {
      setPending(false);
    }
  }

  return (
    <AuthLayout title="Neues Passwort" subtitle="Setze ein neues Passwort.">
      <form onSubmit={onSubmit} className="space-y-4" data-testid="reset-form">
        <Field label="Neues Passwort" htmlFor="password" hint="Mindestens 10 Zeichen.">
          <PasswordInput
            id="password"
            value={password}
            onChange={setPassword}
            autoComplete="new-password"
            required
            minLength={10}
            disabled={pending}
          />
        </Field>
        {error && <FormError message={error} />}
        <PrimaryButton pending={pending} testid="submit-reset">
          Passwort setzen
        </PrimaryButton>
        <div className="text-sm text-center pt-1">
          <Link to="/login" className="text-accent hover:underline">
            ← Zurück zum Login
          </Link>
        </div>
      </form>
    </AuthLayout>
  );
}
